import { useId, useState } from 'react';

import { Button, Checkbox, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { loiTheoConTro } from '../../vai-tro/nghiep-vu/vai-tro';
import { NHAN_COT_LOC, NHAN_KIEU_DU_LIEU, NHAN_VI_TRI } from '../constants';
import { cotLocDaDung, kiemKhoa, tinhPatch } from '../nghiep-vu/loai-chung-tu';
import { DENORM_TARGETS, FIELD_DATA_TYPES, FIELD_POSITIONS } from '../types';
import type { DenormTarget, FieldDataType, FieldPosition, OcrField, OcrFieldInput } from '../types';

const O_TREN_FORM =
  /^\/(field_key|label|data_type|position|is_required|is_unique|validation_regex|description|source_label|denorm_target)$/;
const KHONG_GAN = '';

type BanNhap = {
  field_key: string;
  label: string;
  data_type: FieldDataType;
  position: FieldPosition;
  is_required: boolean;
  is_unique: boolean;
  validation_regex: string;
  description: string;
  source_label: string;
  denorm_target: DenormTarget | null;
};

function tuTruong(f: OcrField | null): BanNhap {
  return {
    field_key: f?.field_key ?? '',
    label: f?.label ?? '',
    data_type: f?.data_type ?? 'TEXT',
    position: f?.position ?? 'HEADER',
    is_required: f?.is_required ?? false,
    is_unique: f?.is_unique ?? false,
    validation_regex: f?.validation_regex ?? '',
    description: f?.description ?? '',
    source_label: f?.source_label ?? '',
    denorm_target: f?.denorm_target ?? null,
  };
}

/** Kiểm biểu thức chính quy ngay ở trình duyệt, cùng luật với backend (`new RegExp`). */
function regexHopLe(s: string): boolean {
  if (!s.trim()) return true;
  try {
    new RegExp(s);
    return true;
  } catch {
    return false;
  }
}

/**
 * Hộp thêm / sửa một trường dữ liệu của loại chứng từ. Sửa thì chỉ gửi trường đổi. Cột lọc nhanh
 * đã có trường khác dùng thì khoá lựa chọn đó kèm tên trường đang giữ (một cột lọc giữ một giá trị).
 */
export function HopTruong({
  open,
  truong,
  tatCa,
  dangGui,
  onClose,
  onLuu,
}: {
  open: boolean;
  /** `null` = thêm mới. */
  truong: OcrField | null;
  tatCa: readonly OcrField[];
  dangGui: boolean;
  onClose: () => void;
  onLuu: (du: Partial<OcrFieldInput>) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const [ban, setBan] = useState<BanNhap>(() => tuTruong(truong));
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: ApiError; truong: Record<string, string> } | null>(null);
  const dangDung = cotLocDaDung(tatCa, truong?.id);

  const doi = <K extends keyof BanNhap>(k: K, v: BanNhap[K]) => {
    setBan((b) => ({ ...b, [k]: v }));
    setLoiServer(null);
  };

  const maKhoa = kiemKhoa(ban.field_key);
  const loiKhoa =
    (daGui && maKhoa ? t(maKhoa === 'trong' ? 'cauHinhOcr.fKhoaTrong' : 'cauHinhOcr.fKhoaSai') : undefined) ??
    loiServer?.truong['/field_key'];
  const loiNhan = (daGui && !ban.label.trim() ? t('cauHinhOcr.fNhanTrong') : undefined) ?? loiServer?.truong['/label'];
  const loiRegex =
    (!regexHopLe(ban.validation_regex) ? t('cauHinhOcr.fRegexSai') : undefined) ?? loiServer?.truong['/validation_regex'];
  const loiKhac = Object.entries(loiServer?.truong ?? {}).filter(([d]) => !O_TREN_FORM.test(d));
  const coLoiTheoO = loiServer && Object.keys(loiServer.truong).length > 0 && loiKhac.length === 0;

  const gui = async () => {
    setDaGui(true);
    if (maKhoa || !ban.label.trim() || !regexHopLe(ban.validation_regex) || dangGui) return;
    const moi: BanNhap = { ...ban, field_key: ban.field_key.trim() };
    const du = truong
      ? tinhPatch<BanNhap>(tuTruong(truong), moi, ['validation_regex', 'description', 'source_label'])
      : tinhPatch<BanNhap>({}, moi, ['validation_regex', 'description', 'source_label']);
    if (truong && Object.keys(du).length === 0) {
      onClose();
      return;
    }
    setLoiServer(null);
    try {
      await onLuu(du as Partial<OcrFieldInput>);
    } catch (err) {
      setLoiServer({ loi: err as ApiError, truong: loiTheoConTro((err as ApiError).details) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(truong ? 'cauHinhOcr.truongSuaTieuDe' : 'cauHinhOcr.truongThemTieuDe')}
      width={680}
      destroyOnHidden
      footer={
        <div className="space-y-3 text-left">
          {loiServer && (
            <LoiTrongHop>
              {coLoiTheoO ? t('cauHinhOcr.loiTheoO') : dichLoi(loiServer.loi)}
              {loiKhac.map(([d, c]) => (
                <span key={d} className="mt-1 block">
                  <span className="font-mono">{d}</span>: {c}
                </span>
              ))}
            </LoiTrongHop>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangGui}>
              {dangGui ? t('chung.dangLuu') : t(truong ? 'cauHinhOcr.luuTruong' : 'cauHinhOcr.themTruong')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('cauHinhOcr.fKhoa')} required hint={t('cauHinhOcr.fKhoaGoiY')} error={loiKhoa}>
            {(p) => (
              <Input
                {...p}
                autoFocus={!truong}
                autoComplete="off"
                className="font-mono"
                maxLength={100}
                placeholder="so_hoa_don"
                value={ban.field_key}
                onChange={(e) => doi('field_key', e.target.value)}
              />
            )}
          </Field>
          <Field label={t('cauHinhOcr.fNhan')} required error={loiNhan}>
            {(p) => (
              <Input {...p} maxLength={255} value={ban.label} onChange={(e) => doi('label', e.target.value)} />
            )}
          </Field>
          <Field label={t('cauHinhOcr.fKieu')} required error={loiServer?.truong['/data_type']}>
            {(p) => (
              <Select<FieldDataType>
                {...p}
                className="w-full"
                value={ban.data_type}
                onChange={(v) => doi('data_type', v)}
                options={FIELD_DATA_TYPES.map((v) => ({ value: v, label: t(NHAN_KIEU_DU_LIEU[v]) }))}
              />
            )}
          </Field>
          <Field label={t('cauHinhOcr.fViTri')} required error={loiServer?.truong['/position']}>
            {(p) => (
              <Select<FieldPosition>
                {...p}
                className="w-full"
                value={ban.position}
                onChange={(v) => doi('position', v)}
                options={FIELD_POSITIONS.map((v) => ({ value: v, label: t(NHAN_VI_TRI[v]) }))}
              />
            )}
          </Field>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox checked={ban.is_required} onChange={(e) => doi('is_required', e.target.checked)}>
            <span className="text-sm text-content-secondary">{t('cauHinhOcr.fBatBuoc')}</span>
          </Checkbox>
          <Checkbox checked={ban.is_unique} onChange={(e) => doi('is_unique', e.target.checked)}>
            <span className="text-sm text-content-secondary">{t('cauHinhOcr.fDuyNhat')}</span>
          </Checkbox>
        </div>
        <Field
          label={t('cauHinhOcr.fCotLoc')}
          hint={t('cauHinhOcr.fCotLocGoiY')}
          error={loiServer?.truong['/denorm_target']}
        >
          {(p) => (
            <Select<string>
              {...p}
              className="w-full"
              value={ban.denorm_target ?? KHONG_GAN}
              onChange={(v) => doi('denorm_target', v === KHONG_GAN ? null : (v as DenormTarget))}
              options={[
                { value: KHONG_GAN, label: t('cauHinhOcr.khongGanCotLoc') },
                ...DENORM_TARGETS.map((v) => ({
                  value: v,
                  disabled: Boolean(dangDung[v]),
                  label: dangDung[v]
                    ? t('cauHinhOcr.cotLocDaDung', { cot: t(NHAN_COT_LOC[v]), ten: dangDung[v]! })
                    : t(NHAN_COT_LOC[v]),
                })),
              ]}
            />
          )}
        </Field>
        <Field label={t('cauHinhOcr.fNhanIn')} hint={t('cauHinhOcr.fNhanInGoiY')} error={loiServer?.truong['/source_label']}>
          {(p) => (
            <Input {...p} maxLength={255} value={ban.source_label} onChange={(e) => doi('source_label', e.target.value)} />
          )}
        </Field>
        <Field label={t('cauHinhOcr.fRegex')} hint={t('cauHinhOcr.fRegexGoiY')} error={loiRegex}>
          {(p) => (
            <Input
              {...p}
              className="font-mono"
              maxLength={1000}
              placeholder="^[0-9]{10}$"
              value={ban.validation_regex}
              onChange={(e) => doi('validation_regex', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('cauHinhOcr.fMoTaTruong')} hint={t('cauHinhOcr.fMoTaTruongGoiY')} error={loiServer?.truong['/description']}>
          {(p) => (
            <Textarea {...p} rows={2} maxLength={1000} value={ban.description} onChange={(e) => doi('description', e.target.value)} />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
